/**
 * KEEPS A DANCER'S COSTUME TAB CURRENT WITHOUT A LIVE CONNECTION (2026-09-26).
 *
 * The dancer sign-in reads its home through one server call and holds no
 * Firestore listener, so a bag the desk scans at 6:40 would still read
 * "Waiting for pickup" on her phone at 6:45. The family side updates live
 * (its costume listener was already open); this is the dancer side's answer.
 *
 * WHEN IT ASKS: the moment the Costumes tab opens, and each time the app
 * comes back to the front while that tab is showing. Never more than once a
 * minute, across every class's tab, however often she flips between them, so
 * a child tapping around costs at most one small read a minute. What it
 * reads is in netlify/functions/dancerPortal.mjs, readDancerCostumeStates.
 *
 * WHICH ANSWER WINS (refute round 2, 2026-09-27). Two sources can speak for
 * the same bag: the home load (the portal's own pickupState) and this
 * refresh. Each carries a time: a refresh answer is stamped when its ask was
 * SENT, and the home load when its payload ARRIVED (portalReceivedAtMs,
 * stamped by DancerHome as it applies the load, because the server sends no
 * time of its own). The newer
 * one wins, and a tie goes to the home load. Both stamps lean the same way on
 * purpose: when in doubt the refresh is treated as older, so it can never
 * outrank a home load that might have read the desk's scan after it did.
 *
 * A failed ask is silent and changes nothing: the last answer and its time
 * stay as they were. So after a failure the tab shows the last successful
 * refresh only if it is still newer than the latest home load, and the home
 * load's own answer otherwise. A failure never makes an answer older than
 * the latest home load outrank it. (Before this, the last answer had no time
 * and always won, so a later home reload could not replace it.)
 */
import { useEffect, useState } from 'react';
import { getDancerPortalCostumeStates, type DancerCostumePickupState } from './dancerSession';

export const COSTUME_STATES_MIN_INTERVAL_MS = 60_000;

export type DancerCostumeStates = Record<string, DancerCostumePickupState>;

/** Due for a fresh ask: never asked, or the last ask is a minute old. */
export function costumeStatesRefreshDue(nowMs: number, lastAskedAtMs: number | null): boolean {
  return lastAskedAtMs === null || nowMs - lastAskedAtMs >= COSTUME_STATES_MIN_INTERVAL_MS;
}

// Module state, so the minute is shared by every class's Costumes tab and
// survives the tab unmounting when she switches classes.
//
// OWNED BY ONE SIGN-IN (refute round 1, 2026-09-27). A dancer sign-out does
// not reload the page, so on a shared phone sister B signs in on the same
// tab sister A just left, and sisters share costume cards. Unkeyed, B's
// first render showed A's "Picked up" and B's own ask was skipped for up to
// a minute. Everything below now belongs to the sign-in whose CSRF token
// asked for it (the server derives the token from the session secret, so it
// is one value per sign-in and never shared by two). A different token, or
// the sign-in ending (forgetDancerCostumeStates, called by the dancer
// session provider), drops the answer, the minute and any ask still in
// flight; an ask that resolves after the switch is thrown away unread.
let owner: string | null = null;
let generation = 0;
let lastAskedAt: number | null = null;
let lastStates: DancerCostumeStates | null = null;
/** When the ask that produced lastStates was sent (epoch ms). */
let lastStatesAskedAt: number | null = null;
let inFlight: Promise<DancerCostumeStates | null> | null = null;

interface OwnedStates { token: string; states: DancerCostumeStates; askedAtMs: number }
type Listener = (owned: OwnedStates) => void;
const listeners = new Set<Listener>();

/**
 * Forget every answer, the shared minute and any ask in flight. Called when
 * the dancer sign-in ends, so nothing of hers survives for the next person.
 */
export function forgetDancerCostumeStates(): void {
  owner = null;
  generation += 1;
  lastAskedAt = null;
  lastStates = null;
  lastStatesAskedAt = null;
  inFlight = null;
}

/** Test-only: forget the shared minute and the last answer. */
export function resetDancerCostumeStatesForTests(): void {
  forgetDancerCostumeStates();
  listeners.clear();
}

/** Make this sign-in the owner, dropping whatever another one left behind. */
function claimFor(csrfToken: string): void {
  if (owner === csrfToken) return;
  forgetDancerCostumeStates();
  owner = csrfToken;
}

async function askIfDue(csrfToken: string, nowMs: number): Promise<void> {
  claimFor(csrfToken);
  if (inFlight || !costumeStatesRefreshDue(nowMs, lastAskedAt)) return;
  const myGeneration = generation;
  lastAskedAt = nowMs;
  const ask = getDancerPortalCostumeStates(csrfToken).catch(() => null);
  inFlight = ask;
  try {
    const states = await ask;
    // A sign-out or another sign-in happened while this was on the wire:
    // the answer is someone else's now, so it is dropped unread.
    if (states && myGeneration === generation && owner === csrfToken) {
      lastStates = states;
      lastStatesAskedAt = nowMs;
      const owned = { token: csrfToken, states, askedAtMs: nowMs };
      listeners.forEach((listener) => listener(owned));
    }
  } finally {
    if (inFlight === ask) inFlight = null;
  }
}

/**
 * True when a refresh answer asked at askedAtMs is newer than the home load
 * that arrived at homeReceivedAtMs. An unknown home time (null) counts as
 * older than any refresh; a tie goes to the home load.
 */
export function refreshAnswerIsNewer(askedAtMs: number, homeReceivedAtMs: number | null): boolean {
  return homeReceivedAtMs === null || askedAtMs > homeReceivedAtMs;
}

/**
 * Her own pickup answers by costume id, or null when the cards should read
 * the home load's own answer: before the first refresh answer arrives, and
 * whenever the latest home load (homeReceivedAtMs, DancerHome's
 * portalReceivedAtMs) is newer than the last refresh answer. Mount it only while
 * the Costumes tab shows. Never returns an answer that belongs to any
 * sign-in other than csrfToken.
 */
export function useDancerCostumeStates(
  csrfToken: string | null,
  enabled: boolean,
  homeReceivedAtMs: number | null = null,
): DancerCostumeStates | null {
  const [owned, setOwned] = useState<OwnedStates | null>(() => (
    csrfToken && owner === csrfToken && lastStates && lastStatesAskedAt !== null
      ? { token: csrfToken, states: lastStates, askedAtMs: lastStatesAskedAt }
      : null
  ));
  useEffect(() => {
    if (!enabled || !csrfToken) return undefined;
    const listener: Listener = (next) => {
      if (next.token === csrfToken) setOwned(next);
    };
    listeners.add(listener);
    void askIfDue(csrfToken, Date.now());
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void askIfDue(csrfToken, Date.now());
      }
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      listeners.delete(listener);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [csrfToken, enabled]);
  // Checked at render, not in an effect, so not even one frame shows an
  // answer from a sign-in that is no longer this one, or one older than the
  // home load the cards already hold.
  if (!owned || !csrfToken || owned.token !== csrfToken) return null;
  return refreshAnswerIsNewer(owned.askedAtMs, homeReceivedAtMs) ? owned.states : null;
}
